/**
 * Metadatos de evidencia de producto — SOLO para el badge del selector de
 * producto (Fase 4.3/4.4). NO es un cálculo financiero: es una consulta de
 * catálogo estático ("¿este producto tiene evidencia real?"), no una
 * cuenta con dinero. Por eso sigue viviendo aquí después de la Fase 5.2 en
 * vez de moverse al backend: no hay float ni redondeo que duplicar.
 *
 * `previsualizarPago` SÍ era un cálculo (monto × factor, redondeo de
 * cuotas) y se eliminó en la Fase 5.2: el pago/total/interés/calendario de
 * un crédito los calcula exclusivamente el backend, vía
 * POST /api/v1/creditos/previsualizar — ver CreditoNuevo.js.
 */
import tablaMaestra from '../data/tablaMaestraPagos.json';

const PRODUCTOS_POR_CODIGO = new Map(tablaMaestra.productos.map((p) => [p.codigo, p]));

/** Metadatos de evidencia/linealidad de un producto, o null si no está en la tabla. */
export function obtenerEvidenciaProducto(codigoProducto) {
  return PRODUCTOS_POR_CODIGO.get(codigoProducto) || null;
}

export function tieneFactorValidado(codigoProducto) {
  const p = obtenerEvidenciaProducto(codigoProducto);
  return !!p && p.linealidad === 'LINEAL_CONFIRMADO' && p.fuente === 'MIDAS_SIMULADOR_PRODUCCION';
}

/**
 * Formatea un importe para mostrar.
 *
 * Acepta number (cálculos locales de previsualización) y string decimal
 * ("1619.23"), que es como la API entrega el dinero desde la Fase 5.1: en
 * el servidor los montos son Decimal exactos y viajan como string para no
 * volver a pasar por un double al cruzar JSON.
 *
 * El formateo sí usa Number, y es correcto: aquí el valor ya sólo se va a
 * pintar. Ningún cálculo debe partir de este resultado.
 */
export function formatoMoneda(valor) {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'string') {
    const raw = valor.trim();
    if (raw === '—' || raw === '-' || raw === 'N/A') return '—';
    const num = Number(raw.replace(/[^0-9.-]+/g, ''));
    if (!Number.isFinite(num)) return raw;
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(num);
  }
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(numero);
}

/**
 * Limpia la denominación de un grupo para la interfaz operativa,
 * separando el nombre canónico principal de notas de procedencia o migración.
 * Preserva la procedencia únicamente en badge secundario/auditoría.
 */
export function limpiarNombreGrupo(nombreRaw) {
  if (!nombreRaw) return { nombre: '—', badge: null };
  let s = String(nombreRaw).trim();
  let badge = null;

  if (/^\[HIST[ÓO]RICO\]\s*/i.test(s)) {
    s = s.replace(/^\[HIST[ÓO]RICO\]\s*/i, '').trim();
    badge = 'HISTÓRICO MIGRADO';
  }

  const match = s.match(/^(.*?)\s*\(([^)]+)\)$/);
  if (match) {
    const mainName = match[1].trim();
    const note = match[2].trim();
    let mappedBadge = badge;
    if (/migraci[óo]n/i.test(note)) {
      mappedBadge = 'HISTÓRICO MIGRADO';
    } else {
      mappedBadge = mappedBadge || note;
    }
    return {
      nombre: mainName,
      badge: mappedBadge
    };
  }

  return { nombre: s, badge };
}

/**
 * Limpia el nombre del producto eliminando prefijos de migración en la UI operativa.
 */
export function limpiarNombreProducto(nombreRaw) {
  if (!nombreRaw) return 'Crédito';
  return String(nombreRaw).replace(/^\[HIST[ÓO]RICO\]\s*/i, '').trim();
}

/**
 * Formatea un número telefónico para presentación en UI.
 * Mapea valores vacíos, nulos o placeholders como '0000000000' a 'Sin teléfono registrado'.
 */
export function formatoTelefono(telefonoRaw) {
  if (!telefonoRaw) return 'Sin teléfono registrado';
  const clean = String(telefonoRaw).trim();
  if (clean === '' || /^0+$/.test(clean) || clean === '—' || clean === 'N/A' || clean === 'null') {
    return 'Sin teléfono registrado';
  }
  return clean;
}

